/*
 * Copyright (C) Fraunhofer IESE 2021-2024 - Pedram (Majid) Jokar, Mher Ter-Tovmasyan,
 * Bestin John, Steffen Hupp, Anna Kleiner, Rafael Aranda Lopez King, Emily Calvet, Philipp Ewen,
 * Matthias Gerbershagen, Milad Chatrangoon, Stefan Schweitzer
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  SLRLabelIcon,
  SLRModal,
  SLRPageHead,
  SLRSpinner,
  SLRTable,
  SLRToast,
  getIcon,
  hasValue,
  isEmptyOrNull,
} from "@slr/shared-library";
import { Invitation, UserMembershipInfo } from "@slr/user-service-external-sdk";
import {
  useAcceptCurrentUserPendingInvitation,
  useEnsureBusinessOrganization,
  useFindMembershipsOfUser,
  useGetCurrentUserProfileWithSolution1,
  useGetPendingCurrentUserInvitations,
  useRejectCurrentUserPendingInvitation,
} from "hooks";
import { userNeedsRegistrationStepTwo } from "hooks/profile/userNeedsRegistrationStepTwo";
import { FC, useCallback, useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { CreateOrganizationButton, Empty, OrganizationModal } from ".";

import { APP_NAME } from "settings";
import "./OrganizationsPage.scss";

export const OrganizationsPage: FC = () => {
  const { t } = useTranslation("translation", {
    keyPrefix: "organizationsPage",
  });

  const navigate = useNavigate();

  const { data: organizations, isPending } = useFindMembershipsOfUser();
  const { data: userProfile } = useGetCurrentUserProfileWithSolution1();
  const {
    mutate: ensureBusinessOrganization,
    isPending: isEnsuringBusinessOrganization,
  } = useEnsureBusinessOrganization();
  const hasTriggeredEnsure = useRef(false);

  useEffect(() => {
    if (
      isPending ||
      isEnsuringBusinessOrganization ||
      hasTriggeredEnsure.current ||
      organizations?.length ||
      userNeedsRegistrationStepTwo(userProfile, organizations)
    ) {
      return;
    }

    hasTriggeredEnsure.current = true;
    ensureBusinessOrganization();
  }, [
    ensureBusinessOrganization,
    isEnsuringBusinessOrganization,
    isPending,
    organizations,
    userProfile,
  ]);

  const { data: pendingInvitations } = useGetPendingCurrentUserInvitations();
  const { mutate: rejectInvitation } = useRejectCurrentUserPendingInvitation();
  const { mutate: acceptInvitation } = useAcceptCurrentUserPendingInvitation();

  const handleInvitationAccept = useCallback(
    (invitation: Invitation) => {
      acceptInvitation({
        invitationId: invitation.id,
        organizationId: invitation.organizationId,
      });
    },
    [acceptInvitation]
  );

  const handleInvitationReject = useCallback(
    (invitation: Invitation) => {
      rejectInvitation({
        invitationId: invitation.id,
        organizationId: invitation.organizationId,
      });
    },
    [rejectInvitation]
  );

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const handleShowCreateModal = (show: boolean) => setShowCreateModal(show);

  const handleOpenOrganization = useCallback(
    (id?: string) => !isEmptyOrNull(id) && navigate(id),
    [navigate]
  );

  const [showInfoModal, setShowInfoModal] = useState<boolean>(false);
  const handleShowInfoModal = (show: boolean) => setShowInfoModal(show);

  if (isPending || isEnsuringBusinessOrganization) {
    return <SLRSpinner />;
  }

  return (
    <>
      {showCreateModal && (
        <OrganizationModal onClose={() => handleShowCreateModal(false)} />
      )}

      {showInfoModal && (
        <SLRModal
          isOpen
          size="lg"
          fullscreen="sm-down"
          className="organizations-page-info"
          title={t("info.title")}
          onClose={() => handleShowInfoModal(false)}
        >
          <h2 className="pb-3">{t("info.content.title")}</h2>

          <p>
            <Trans
              t={t}
              i18nKey="info.content.message"
              values={{ appName: APP_NAME }}
            />
          </p>
        </SLRModal>
      )}

      <Container className="organizations-page table-responsive-md">
        <SLRPageHead
          className="pb-5"
          labels={{
            title: (
              <div className="d-flex flex-row gap-3">
                {t("organizations")}
                <SLRLabelIcon
                  width="36"
                  icon={getIcon("fas", "info-circle")}
                  onClick={() => handleShowInfoModal(true)}
                />
              </div>
            ),
            description: t("description"),
          }}
        />
        {hasValue(pendingInvitations) && (
          <Row>
            <Col className="pb-5">
              {pendingInvitations.map((invitation) => (
                <SLRToast
                  key={invitation.id}
                  toastId={invitation.organizationName}
                  title={t("invitation.title", {
                    organizationName: invitation.organizationName,
                  })}
                  type="info"
                  closeable={false}
                >
                  <>
                    {t("invitation.contentTop", {
                      organizationName: invitation.organizationName,
                    })}
                    <br />
                    {t("invitation.contentBottom")}
                    <div className="mt-3 gap-3 d-flex flex-wrap">
                      <Button
                        variant="primary"
                        onClick={() => {
                          handleInvitationAccept(invitation);
                        }}
                      >
                        {t("invitation.accept")}
                      </Button>
                      <Button
                        variant="outline-danger"
                        onClick={() => {
                          handleInvitationReject(invitation);
                        }}
                      >
                        {t("invitation.reject")}
                      </Button>
                    </div>
                  </>
                </SLRToast>
              ))}
            </Col>
          </Row>
        )}
        <Row>
          <Col xs={12}>
            <SLRTable<UserMembershipInfo & { role: string }>
              emptyView={
                <Empty
                  labels={
                    t("empty", { returnObjects: true }) as {
                      top: string;
                      bottom: string;
                    }
                  }
                  onCreate={() => handleShowCreateModal(true)}
                />
              }
              sortIcon={getIcon("fas", "long-arrow-up")}
              header={{
                title: t("memberships"),
                children: (
                  <CreateOrganizationButton
                    onClick={() => handleShowCreateModal(true)}
                  />
                ),
              }}
              initialSort={{
                accessor: "organizationName",
                sortOrder: "asc",
              }}
              columns={[
                {
                  label: t("organizations"),
                  accessor: "organizationName",
                  sortable: true,
                },
                {
                  label: (
                    <>
                      <div className="d-none" aria-hidden>
                        {t("numberOfMembers")}
                      </div>
                      <FontAwesomeIcon
                        icon={getIcon("fas", "users")}
                        aria-label={t("numberOfMembers")}
                      />
                    </>
                  ),
                  accessor: "numberOfMembers",
                  sortable: true,
                },
                {
                  label: t("role"),
                  accessor: "role",
                  sortable: false,
                  cellFn: ({ value }: { value: "admin" | "member" }) =>
                    t(`${value}`),
                },
              ]}
              data={organizations}
              onRowClick={({ organizationId }) =>
                handleOpenOrganization(organizationId)
              }
              responsive="md"
            />
          </Col>
        </Row>
      </Container>
    </>
  );
};
